import { apiErrorMessage, useUploadImage } from '@agos/api-client'
import type { CategoryWithTranslations, Product } from '@agos/api-client/model'
import { T, tt } from '@agos/shared'
import { cn, toast } from '@agos/ui'
import { ChevronDown, ImagePlus, ListFilter, Loader2, Lock, Plus, Unlock, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import type { BurgerBoxSlot } from './types'

/** The category dropdown used for both the trigger category and each slot. */
export function CategoryPicker({
  value,
  onChange,
  categories,
  placeholder,
}: {
  value: number | null
  onChange: (categoryId: number | null) => void
  categories: CategoryWithTranslations[]
  placeholder?: string
}) {
  return (
    <div className="relative">
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
        className="w-full appearance-none px-3 py-2.5 pr-8 rounded-xl bg-background border border-border text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20 transition-all"
      >
        <option value="">{placeholder ?? tt('- nevybrané -')}</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
    </div>
  )
}

/** The per-slot image, shown to the customer above that slot's choices. */
export function SlotImageUpload({
  imageUrl,
  onUpdate,
}: {
  imageUrl?: string | null
  onUpdate: (url: string | null) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const uploadImage = useUploadImage()

  const upload = (file: File) => {
    // §9.796 - `usePostUploadImage` sent `JSON.stringify(form)`, which is `{}`, so no slot image
    // ever reached the server. `useUploadImage` sends the file as a real form part.
    uploadImage.mutate(
      file,
      {
        onSuccess: (response) => {
          const url = response?.url
          if (url) onUpdate(url)
          else {
            // FB-022: legacy swallows this entirely — `catch {}` — and the
            // button just returns to rest with no image and no explanation.
            toast({
              variant: 'destructive',
              title: tt('Nahrávanie zlyhalo'),
              description: tt('Server nevrátil adresu obrázka.'),
            })
          }
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Nahrávanie zlyhalo'),
            description: apiErrorMessage(caught, tt('Obrázok sa nepodarilo nahrať.')),
          }),
      },
    )
  }

  return (
    <div className="pl-7 flex items-center gap-2">
      {imageUrl ? (
        <div className="relative flex-shrink-0">
          <img
            src={imageUrl}
            alt={tt('Obrázok slotu')}
            className="w-12 h-12 rounded-xl object-cover border border-border"
          />
          <button
            type="button"
            onClick={() => onUpdate(null)}
            title={tt('Odstrániť obrázok')}
            aria-label={tt('Odstrániť obrázok')}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="w-12 h-12 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center flex-shrink-0">
          <ImagePlus className="w-5 h-5 text-muted-foreground/50" />
        </div>
      )}

      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Obrázok slotu</T>
        </p>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploadImage.isPending}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-semibold hover:bg-muted/80 border border-border transition-colors disabled:opacity-50"
        >
          {uploadImage.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <ImagePlus className="w-3 h-3" />
          )}
          {uploadImage.isPending
            ? tt('Nahrávam...')
            : imageUrl
              ? tt('Zmeniť obrázok')
              : tt('Nahrať obrázok')}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) upload(file)
            event.target.value = ''
          }}
        />
      </div>
    </div>
  )
}

/**
 * Which products a slot offers.
 *
 * With no presets the customer sees every active product in the slot's
 * category. Add presets and they see only those. A preset marked **fixed** is
 * always included rather than chosen.
 */
export function SlotPresetEditor({
  slot,
  products,
  onUpdate,
}: {
  slot: BurgerBoxSlot
  products: Product[]
  onUpdate: (patch: Partial<BurgerBoxSlot>) => void
}) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const categoryProducts = useMemo(
    () =>
      slot.categoryId
        ? products.filter((product) => product.categoryId === slot.categoryId && product.active)
        : [],
    [products, slot.categoryId],
  )

  const presetProductIds = useMemo(
    () => new Set(slot.presetItems.map((preset) => preset.productId)),
    [slot.presetItems],
  )

  const productById = (productId: number) => products.find((product) => product.id === productId)

  const togglePreset = (productId: number) =>
    onUpdate({
      presetItems: presetProductIds.has(productId)
        ? slot.presetItems.filter((preset) => preset.productId !== productId)
        : [...slot.presetItems, { productId, fixed: false }],
    })

  const toggleFixed = (productId: number) =>
    onUpdate({
      presetItems: slot.presetItems.map((preset) =>
        preset.productId === productId ? { ...preset, fixed: !preset.fixed } : preset,
      ),
    })

  if (!slot.categoryId) return null

  return (
    <div className="pl-7 space-y-2">
      <div className="flex items-center gap-2">
        <ListFilter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-xs text-muted-foreground font-medium">
          {slot.presetItems.length === 0 ? (
            <T>Zákazník vidí všetky produkty z kategórie</T>
          ) : (
            <>
              <T>Prednastavené produkty:</T> {slot.presetItems.length}
            </>
          )}
        </span>
        <button
          type="button"
          onClick={() => setIsPickerOpen((open) => !open)}
          className={cn(
            'ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all',
            isPickerOpen
              ? 'bg-primary/15 text-primary border-primary/30'
              : 'bg-muted/60 text-muted-foreground border-transparent hover:border-border hover:text-foreground',
          )}
        >
          {isPickerOpen ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {isPickerOpen ? tt('Zavrieť') : tt('Nastaviť produkty')}
        </button>
      </div>

      {slot.presetItems.length > 0 && (
        <div className="space-y-1.5">
          {slot.presetItems.map((preset) => {
            const product = productById(preset.productId)
            if (!product) return null
            return (
              <div
                key={preset.productId}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all',
                  preset.fixed
                    ? 'bg-orange-500/8 border-orange-500/30'
                    : 'bg-primary/5 border-primary/20',
                )}
              >
                <span className="flex-1 min-w-0 truncate text-foreground">
                  <T>{product.name}</T>
                </span>
                <button
                  type="button"
                  onClick={() => toggleFixed(preset.productId)}
                  title={preset.fixed ? tt('Vždy zahrnuté') : tt('Zákazník si vyberá')}
                  className={cn(
                    'flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold border transition-all flex-shrink-0',
                    preset.fixed
                      ? 'bg-orange-500/15 text-orange-500 border-orange-500/30'
                      : 'bg-muted text-muted-foreground border-transparent hover:border-border',
                  )}
                >
                  {preset.fixed ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  {preset.fixed ? tt('Pevné') : tt('Voliteľné')}
                </button>
                <button
                  type="button"
                  aria-label={tt('Odstrániť')}
                  onClick={() => togglePreset(preset.productId)}
                  className="p-1 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {isPickerOpen && (
        <div className="border border-border rounded-xl p-2 max-h-56 overflow-y-auto space-y-1 bg-background/50">
          {categoryProducts.length === 0 && (
            <p className="text-xs text-muted-foreground py-3 text-center">
              <T>V tejto kategórii nie sú žiadne aktívne produkty.</T>
            </p>
          )}
          {categoryProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              onClick={() => product.id !== undefined && togglePreset(product.id)}
              className={cn(
                'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-left transition-colors',
                product.id !== undefined && presetProductIds.has(product.id)
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'hover:bg-muted/60 text-muted-foreground',
              )}
            >
              <span className="flex-1 min-w-0 truncate">
                <T>{product.name}</T>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
